// hooks/useMediaSocket.js

import { useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

export default function useMediaSocket({
  socket,

  // callbacks (names match what GroupChat.js passes)
  mediaOnOptimisticPost,
  mediaOnNewFollowingPost,
  mediaOnNewGlobalPost,
  mediaOnLikeUpdate,
  mediaOnUnreadLikeUpdate,
  mediaOnNewComment,
  mediaOnCommentCountUpdate,
  onUnreadUpdate,
  mediaOnViewUpdate,
  onViewProgress,
  mediaOnCommentLikeUpdate,
    mediaOnCommentViewUpdate,
  onUnreadCommentLikeUpdate,
  onClearNotificationsResult,
  mediaOnNotificationsCleared,
  onSupportResult,
  onFeedError,
  mediaOnError,
}) {
  useEffect(() => {
    if (!socket) return;

    const events = {
      "media-new-post-following": mediaOnNewFollowingPost,
      "media-new-post-global": mediaOnNewGlobalPost,
      "media-like-update": mediaOnLikeUpdate,
      "media-unread-like-update": mediaOnUnreadLikeUpdate,
      "media-new-comment": mediaOnNewComment,
      "media-comment-count-update": mediaOnCommentCountUpdate,
      "media-unread-update": onUnreadUpdate,
      "media-view-update": mediaOnViewUpdate,
      "media-view-progress": onViewProgress,
      "media-comment-like-update": mediaOnCommentLikeUpdate,
        "media-comment-view-update": mediaOnCommentViewUpdate,
      "media-unread-comment-like-update": onUnreadCommentLikeUpdate,
      "media-clear-all-notifications-result":
        onClearNotificationsResult,
      // server emits "media-all-notifications-cleared"
      "media-all-notifications-cleared":
        mediaOnNotificationsCleared,
      "media-support-post-result": onSupportResult,
      "feed-error": onFeedError,
      "media-error": mediaOnError,
    };

    Object.entries(events).forEach(([event, fn]) => {
      if (typeof fn === "function") socket.on(event, fn);
    });

    return () => {
      Object.entries(events).forEach(([event, fn]) => {
        if (typeof fn === "function") socket.off(event, fn);
      });
    };
  }, [
    socket,
    mediaOnNewFollowingPost,
    mediaOnNewGlobalPost,
    mediaOnLikeUpdate,
    mediaOnUnreadLikeUpdate,
    mediaOnNewComment,
    mediaOnCommentCountUpdate,
    onUnreadUpdate,
    mediaOnViewUpdate,
    onViewProgress,
    mediaOnCommentLikeUpdate,
       mediaOnCommentViewUpdate,
    onUnreadCommentLikeUpdate,
    onClearNotificationsResult,
    mediaOnNotificationsCleared,
    onSupportResult,
    onFeedError,
    mediaOnError,
  ]);

  /* =========================================================
     MEDIA ACTIONS
  ========================================================= */
 const mediaCreatePost = useCallback(
  (payload = {}) => {
    if (!socket || !payload.chatId || !payload.authorUsername) return;

    const postId = payload.postId || uuidv4();

    /*
     * Post category:
     * type = standard | series | stem
     *
     * mediaType remains the actual media format:
     * video | image | audio
     */
    const postType = [
      "standard",
      "series",
      "stem",
    ].includes(payload.type)
      ? payload.type
      : "standard";

    const seriesId =
      typeof payload.seriesId === "string" &&
      payload.seriesId.trim()
        ? payload.seriesId.trim()
        : null;

    const seriesTitle =
      typeof payload.seriesTitle === "string" &&
      payload.seriesTitle.trim()
        ? payload.seriesTitle.trim()
        : null;

    const seasonNumber =
      Number.isFinite(Number(payload.seasonNumber)) &&
      Number(payload.seasonNumber) > 0
        ? Number(payload.seasonNumber)
        : null;

    const episodeNumber =
      Number.isFinite(Number(payload.episodeNumber)) &&
      Number(payload.episodeNumber) > 0
        ? Number(payload.episodeNumber)
        : null;

    const optimisticPost = {
      postId,
      isOptimistic: true,

      author: {
        chatId: payload.chatId,
        username: payload.authorUsername.replace(/^@/, ""),
        displayName: payload.authorDisplayName || null,
        profileImageUrl: payload.authorProfileImageUrl || null,
      },

      text: payload.text || "",
      caption: payload.caption || payload.text || "",

      media: payload.media || [],

      mediaType:
        payload.mediaType ||
        payload.media?.[0]?.type ||
        null,

      coverThumbnail: payload.coverThumbnail || null,
      duration: payload.duration ?? 0,
      aspectRatio: payload.aspectRatio ?? null,

      /*
       * New media post model fields
       */
      type: postType,
      seriesId: postType === "series" ? seriesId : null,
      seriesTitle: postType === "series" ? seriesTitle : null,
      seasonNumber:
        postType === "series" ? seasonNumber : null,
      episodeNumber:
        postType === "series" ? episodeNumber : null,

      sound: payload.sound || null,
      visibility: payload.visibility || "public",

      likesCount: 0,
      commentsCount: 0,
      viewsCount: 0,
      supportsCount: 0,
      supportsAmount: 0,
      repostsCount: 0,

      likedByMe: false,

      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Immediately show it
    mediaOnOptimisticPost?.(optimisticPost);

    /*
     * Send the new post category fields to
     * media-create-post on the backend.
     */
    socket.emit("media-create-post", {
      ...payload,
      postId,

      type: postType,
      seriesId:
        postType === "series"
          ? seriesId
          : null,
      seriesTitle:
        postType === "series"
          ? seriesTitle
          : null,
      seasonNumber:
        postType === "series"
          ? seasonNumber
          : null,
      episodeNumber:
        postType === "series"
          ? episodeNumber
          : null,
    });

    return postId;
  },
  [socket, mediaOnOptimisticPost]
);

  const mediaLikePost = useCallback(
    (postId) => {
      socket?.emit("media-like-post", { postId });
    },
    [socket]
  );

  const mediaAddComment = useCallback(
    (payload = {}) => {
      if (!socket || !payload.postId) return;

      const commentId = payload.commentId || uuidv4();

      socket.emit("media-add-comment", {
        ...payload,
        commentId,
      });

      return commentId;
    },
    [socket]
  );

  const mediaTrackView = useCallback(
    (payload) => {
      socket?.emit("media:view", payload);
    },
    [socket]
  );

  const mediaLikeComment = useCallback(
    (commentId) => {
      socket?.emit("media-like-comment", { commentId });
    },
    [socket]
  );

  const mediaClearNotifications = useCallback(() => {
    socket?.emit("media-clear-all-notifications");
  }, [socket]);

  const mediaSupportPost = useCallback(
    (payload) => {
      socket?.emit("media-support-post", payload);
    },
    [socket]
  );

  return {
    mediaCreatePost,
    mediaLikePost,
    mediaAddComment,
    mediaTrackView,
    mediaLikeComment,
    mediaClearNotifications,
    mediaSupportPost,
  };
}